'use client';

import React from 'react';
import { Menu, Bot, Square, Globe } from 'lucide-react';
import { useI18n } from '@/shared/providers/I18nProvider';
import { LanguageSwitcherSheet } from '@/shared/ui/LanguageSwitcherSheet';
import { AuthButton } from '@/shared/ui/AuthButton';

type ChatHeaderProps = {
  isBusy: boolean;
  messagesCount: number;
  onStop?: () => void;
  onOpenDrawer?: () => void;
};

export function ChatHeader({
  isBusy,
  messagesCount,
  onStop,
  onOpenDrawer,
}: ChatHeaderProps) {
  const { dict } = useI18n();
  const [isLanguageOpen, setIsLanguageOpen] = React.useState(false);
  const name = dict.common.appName;
  const status = isBusy ? 'typing' : 'online';
  const statusText = dict.chat.status;

  return (
    <header className='bg-card/80 border-border/50 sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3 backdrop-blur-xl'>
      <div className='flex items-center gap-3'>
        {onOpenDrawer ? (
          <button
            className='hover:bg-secondary/80 -ml-2 rounded-full p-2 transition-colors'
            aria-label='대화 목록'
            onClick={onOpenDrawer}
            disabled={isBusy && messagesCount === 0}
          >
            <Menu className='text-primary h-5 w-5' />
          </button>
        ) : null}

        <div className='flex items-center gap-3'>
          <div className='relative'>
            <div
              className='ring-background bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full shadow-sm ring-2'
              aria-hidden
            >
              <Bot className='text-primary h-5 w-5' />
            </div>
            {status === 'online' && (
              <span className='border-card absolute right-0 bottom-0 h-3 w-3 rounded-full border-2 bg-green-500' />
            )}
          </div>

          <div className='flex flex-col'>
            <span className='text-foreground text-[15px] font-semibold'>{name}</span>
            <span className='text-muted-foreground text-xs'>{statusText[status]}</span>
          </div>
        </div>
      </div>

      <div className='flex items-center gap-1'>
        {isBusy && onStop ? (
          <button
            onClick={onStop}
            className='hover:bg-secondary/80 rounded-full p-2 transition-colors'
            aria-label={dict.chat.header.stopAria}
            type='button'
          >
            <Square className='text-destructive h-5 w-5' />
          </button>
        ) : null}
        <AuthButton />
        <button
          className='hover:bg-secondary/80 rounded-full p-2 transition-colors'
          aria-label={dict.chat.header.languageAria}
          onClick={() => setIsLanguageOpen(true)}
          type='button'
        >
          <Globe className='text-primary h-5 w-5' />
        </button>
      </div>

      <LanguageSwitcherSheet
        open={isLanguageOpen}
        onClose={() => setIsLanguageOpen(false)}
      />
    </header>
  );
}
